import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { EXAM_PANORAMA_BLOCK_ENABLED } from '../../shared/lib/features';
import { formatBytes, formatDate, formatDateTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/Button';
import { Card, ErrorText, Spinner } from '../../shared/ui/primitives';
import { deleteSnapshot } from '../snapshots/api';
import { useSession } from '../auth/session';
import {
  buildPanorama,
  createComment,
  deleteComment,
  deleteExamination,
  downloadExaminationArchive,
  getExaminationDownload,
  getExamination,
  listComments,
  updateComment,
} from './api';
import { DocPreviewModal } from './DocPreviewModal';
import { PanoramaCurveEditor } from './PanoramaCurveEditor';
import { SharePanel } from './SharePanel';
import type { ExaminationComment, ExaminationFile } from './types';
import { UploadPanel } from './UploadPanel';

/** Миниатюра файла: JPEG-тумбнейл от воркера либо заглушка с типом. */
function FileThumb({ file, label }: { file?: ExaminationFile; label?: string }) {
  const box = 'h-14 w-14 shrink-0 rounded-lg border border-line object-cover';
  if (file?.thumbnailUrl) {
    return <img src={file.thumbnailUrl} alt="" loading="lazy" className={`${box} bg-black`} />;
  }
  return (
    <div className={`${box} flex items-center justify-center bg-canvas text-[10px] font-semibold text-muted`}>
      {label ?? file?.kind.toUpperCase() ?? '—'}
    </div>
  );
}

type Row =
  | { kind: 'file'; file: ExaminationFile }
  | { kind: 'series'; seriesUid: string; files: ExaminationFile[] };

/** Файлы одной DICOM-серии из ДВУХ И БОЛЕЕ .dcm сворачиваются в одну строку
 *  (том листается во вьювере). Одиночный многокадровый .dcm с SeriesInstanceUID
 *  остаётся обычным файлом — его кадры листаются на странице снимка. */
function groupRows(files: ExaminationFile[]): Row[] {
  const bySeries = new Map<string, ExaminationFile[]>();
  for (const f of files) {
    if (f.seriesUid) {
      const arr = bySeries.get(f.seriesUid) ?? [];
      arr.push(f);
      bySeries.set(f.seriesUid, arr);
    }
  }
  const rows: Row[] = [];
  const emitted = new Set<string>();
  for (const f of files) {
    const group = f.seriesUid ? bySeries.get(f.seriesUid) : undefined;
    if (group && group.length >= 2) {
      if (!emitted.has(f.seriesUid!)) {
        emitted.add(f.seriesUid!);
        rows.push({ kind: 'series', seriesUid: f.seriesUid!, files: group });
      }
    } else {
      rows.push({ kind: 'file', file: f });
    }
  }
  return rows;
}

export function ExaminationDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';
  const [downloadError, setDownloadError] = useState('');
  const [panoramaError, setPanoramaError] = useState('');
  const [curveEditorOpen, setCurveEditorOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<ExaminationFile | null>(null);

  const query = useQuery({
    queryKey: ['examination', id],
    queryFn: () => getExamination(id),
    enabled: Boolean(id),
    // Пока какой-либо файл строится (напр. панорама) — подтягиваем обновления.
    refetchInterval: (q) =>
      q.state.data?.files.some((f) => f.status === 'processing') ? 2500 : false,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['examination', id] });
    queryClient.invalidateQueries({ queryKey: ['patient'] });
  };

  const downloadJob = useQuery({
    queryKey: ['examination-download', id],
    queryFn: () => getExaminationDownload(id),
    enabled: Boolean(id),
    refetchInterval: (q) => q.state.data?.status === 'preparing' ? 2000 : false,
  });

  const download = useMutation({
    mutationFn: () => downloadExaminationArchive(id),
    onError: (e) => setDownloadError(apiErrorMessage(e, 'Не удалось скачать архив')),
    onSuccess: (job) => {
      setDownloadError('');
      queryClient.setQueryData(['examination-download', id], job);
      if (job.status === 'ready' && job.url) window.location.assign(job.url);
    },
  });

  const panorama = useMutation({
    mutationFn: () => buildPanorama(id),
    onMutate: () => setPanoramaError(''),
    onError: (e) =>
      setPanoramaError(apiErrorMessage(e, 'Не удалось запустить построение панорамы')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['examination', id] }),
  });

  const remove = useMutation({
    mutationFn: () => deleteExamination(id),
    onSuccess: () => {
      const patientId = query.data?.examination.patientId;
      queryClient.invalidateQueries({ queryKey: ['patient'] });
      navigate(patientId ? `/patients/${patientId}` : '/patients', { replace: true });
    },
  });

  const removeFile = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((sid) => deleteSnapshot(sid))).then(() => {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['examination', id] });
      queryClient.invalidateQueries({ queryKey: ['patient'] });
    },
  });

  const files = useMemo(() => query.data?.files ?? [], [query.data]);
  const rows = useMemo(() => groupRows(files), [files]);
  // Текстовые вложения (txt/html/xml) — отдельный блок «Файлы», снимки/DICOM — «Снимки».
  const snapshotRows = useMemo(
    () => rows.filter((r) => r.kind === 'series' || r.file.kind !== 'doc'),
    [rows],
  );
  const docRows = useMemo(
    () => rows.filter((r): r is Extract<Row, { kind: 'file' }> => r.kind === 'file' && r.file.kind === 'doc'),
    [rows],
  );
  const snapshotCount = files.length - docRows.length;
  // 3D-данные бывают многофайловой серией ИЛИ одним многокадровым .dcm (весь
  // том в одном файле — так отдаёт большинство КЛКТ-аппаратов).
  const hasDicomSeries = rows.some(
    (r) =>
      (r.kind === 'series' && r.files[0].kind === '3d') ||
      (r.kind === 'file' && r.file.kind === '3d' && r.file.frames > 1),
  );
  const processing = files.some((f) => f.status === 'processing');
  // Модальность готового файла-панорамы (см. backend queue.PanoramaModality).
  const panoramaFile = files.find((f) => f.modality === 'Проекция' && f.status === 'ready');

  if (query.isLoading) return <Spinner />;
  if (query.isError || !query.data) {
    return <ErrorText>{apiErrorMessage(query.error, 'Обследование недоступно')}</ErrorText>;
  }

  const { examination, patientName, patientEmail, canShare } = query.data;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Link
          to={`/patients/${examination.patientId}`}
          className="text-sm font-medium text-accent hover:text-accent-600"
        >
          ← К карточке пациента
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {patientName && (
              <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                {patientName}
              </h1>
            )}
            <p className="text-base font-medium text-ink">
              Обследование от {formatDate(examination.examinedAt)}
              {examination.modality && (
                <span className="ml-2 text-base font-normal text-muted">{examination.modality}</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={download.isPending || downloadJob.data?.status === 'preparing' || files.length === 0}
              onClick={() => download.mutate()}
            >
              {download.isPending || downloadJob.data?.status === 'preparing' ? 'Готовим архив…' : downloadJob.data?.status === 'ready' ? 'Скачать готовый архив' : 'Подготовить архив'}
            </Button>
            {canShare && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={files.length === 0}
                onClick={() => setShareOpen((v) => !v)}
              >
                Отправить на почту
              </Button>
            )}
            {isAdmin && hasDicomSeries && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={panorama.isPending || processing}
                onClick={() => panorama.mutate()}
              >
                {panorama.isPending || processing
                  ? 'Строим панораму…'
                  : panoramaFile
                    ? 'Перестроить панораму'
                    : 'Построить панораму'}
              </Button>
            )}
            {isAdmin && hasDicomSeries && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={processing}
                onClick={() => setCurveEditorOpen((v) => !v)}
              >
                {curveEditorOpen ? 'Скрыть подгонку дуги' : 'Настроить дугу вручную'}
              </Button>
            )}
            {isAdmin && (
              <Button
                type="button"
                size="sm"
                variant="danger"
                onClick={() => {
                  if (window.confirm('Удалить обследование со всеми файлами? Действие необратимо.')) {
                    remove.mutate();
                  }
                }}
              >
                Удалить
              </Button>
            )}
          </div>
        </div>
        {examination.note && <p className="text-sm text-muted whitespace-pre-wrap">{examination.note}</p>}
        {downloadError && <ErrorText>{downloadError}</ErrorText>}
        {downloadJob.data?.status === 'preparing' && (
          <p className="text-sm text-muted" role="status">Архив готовится на сервере. Можно закрыть страницу и вернуться позже.</p>
        )}
        {downloadJob.data?.status === 'failed' && (
          <ErrorText>Не удалось подготовить архив. Нажмите «Подготовить архив», чтобы повторить.</ErrorText>
        )}
        {panoramaError && <ErrorText>{panoramaError}</ErrorText>}
        {removeFile.isError && (
          <ErrorText>{apiErrorMessage(removeFile.error, 'Не удалось удалить файл')}</ErrorText>
        )}
      </div>

      {shareOpen && canShare && (
        <SharePanel
          examinationId={id}
          defaultEmail={patientEmail}
          onClose={() => setShareOpen(false)}
        />
      )}

      {curveEditorOpen && (
        <PanoramaCurveEditor
          examinationId={id}
          onClose={() => setCurveEditorOpen(false)}
          onBuilt={() => {
            setCurveEditorOpen(false);
            refresh();
          }}
        />
      )}

      {EXAM_PANORAMA_BLOCK_ENABLED && panoramaFile && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-ink">Панорама</h2>
          <a
            href={`/viewer/snapshots/${panoramaFile.id}`}
            className="block overflow-hidden rounded-2xl border border-line bg-black"
          >
            <img
              src={panoramaFile.previewUrl || panoramaFile.viewUrl}
              alt="Панорама"
              className="max-h-[420px] w-full object-contain"
            />
          </a>
        </div>
      )}

      {isAdmin && <UploadPanel examinationId={id} onChanged={refresh} />}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">Снимки ({snapshotCount})</h2>
        {snapshotRows.length > 0 ? (
          <Card className="divide-y divide-line overflow-hidden">
            {snapshotRows.map((row) => {
              const onDelete =
                isAdmin && !removeFile.isPending
                  ? (ids: string[], label: string) => {
                      const msg =
                        ids.length > 1
                          ? `Удалить серию «${label}» (${ids.length} срезов)? Действие необратимо.`
                          : `Удалить «${label}»? Действие необратимо.`;
                      if (window.confirm(msg)) removeFile.mutate(ids);
                    }
                  : undefined;
              return row.kind === 'series' ? (
                <SeriesRow key={`s-${row.seriesUid}`} examId={id} row={row} onDelete={onDelete} />
              ) : (
                <FileRow
                  key={row.file.id}
                  file={row.file}
                  onPreview={() => setPreviewFile(row.file)}
                  onDelete={onDelete}
                  showDownload={isAdmin}
                />
              );
            })}
          </Card>
        ) : (
          <p className="text-sm text-muted">
            {isAdmin ? 'Снимков пока нет — загрузите их выше.' : 'Снимков пока нет.'}
          </p>
        )}
      </div>

      <CommentsSection examinationId={id} />

      {isAdmin && (
        <FilesSection
          rows={docRows}
          onPreview={setPreviewFile}
          onDelete={
            !removeFile.isPending
              ? (ids, label) => {
                  const msg =
                    ids.length > 1
                      ? `Удалить файлы «${label}» (${ids.length})? Действие необратимо.`
                      : `Удалить «${label}»? Действие необратимо.`;
                  if (window.confirm(msg)) removeFile.mutate(ids);
                }
              : undefined
          }
        />
      )}

      {previewFile && (
        <DocPreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </section>
  );
}

/** Сворачиваемый блок текстовых вложений (txt/html/xml) — виден только администратору. */
function FilesSection({
  rows,
  onPreview,
  onDelete,
}: {
  rows: Extract<Row, { kind: 'file' }>[];
  onPreview: (file: ExaminationFile) => void;
  onDelete?: (ids: string[], label: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-lg font-semibold text-ink"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
        >
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Файлы ({rows.length})
      </button>
      {open &&
        (rows.length > 0 ? (
          <Card className="divide-y divide-line overflow-hidden">
            {rows.map((row) => (
              <FileRow
                key={row.file.id}
                file={row.file}
                onPreview={() => onPreview(row.file)}
                onDelete={onDelete}
                showDownload
              />
            ))}
          </Card>
        ) : (
          <p className="text-sm text-muted">Файлов нет.</p>
        ))}
    </div>
  );
}

function FileRow({
  file: f,
  onPreview,
  onDelete,
  showDownload = false,
}: {
  file: ExaminationFile;
  onPreview: () => void;
  onDelete?: (ids: string[], label: string) => void;
  /** Скачивание отдельного файла — только для админа; врач качает архивом целиком (там же txt/xml). */
  showDownload?: boolean;
}) {
  // doc (txt/xml/html) открывается модалкой предпросмотра, остальное — во вьювере.
  const isDoc = f.kind === 'doc';
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      {isDoc ? (
        <button type="button" onClick={onPreview} className="shrink-0" aria-label="Просмотр файла">
          <FileThumb file={f} />
        </button>
      ) : (
        <a href={`/viewer/snapshots/${f.id}`} className="shrink-0">
          <FileThumb file={f} />
        </a>
      )}
      <div className="min-w-0 flex-1">
        {isDoc ? (
          <button
            type="button"
            onClick={onPreview}
            className="block max-w-full truncate text-left font-medium text-ink hover:text-accent"
          >
            {f.originalName || f.title}
          </button>
        ) : (
          <a
            href={`/viewer/snapshots/${f.id}`}
            className="block truncate font-medium text-ink hover:text-accent"
          >
            {f.originalName || f.title}
          </a>
        )}
        <span className="text-xs text-muted">
          {f.kind.toUpperCase()}
          {f.frames > 1 ? ` · кадров: ${f.frames}` : ''} · {formatBytes(f.originalSize)} · {f.status}
        </span>
      </div>
      {isDoc ? (
        <button
          type="button"
          onClick={onPreview}
          className="shrink-0 text-xs font-medium text-accent hover:text-accent-600"
        >
          Просмотр
        </button>
      ) : (
        <a
          href={`/viewer/snapshots/${f.id}`}
          className="shrink-0 text-xs font-medium text-accent hover:text-accent-600"
        >
          Просмотр
        </a>
      )}
      {showDownload && (
        <a
          href={f.downloadUrl}
          className="shrink-0 text-xs font-medium text-accent hover:text-accent-600"
          target="_blank"
          rel="noreferrer"
        >
          Скачать
        </a>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete([f.id], f.originalName || f.title)}
          className="shrink-0 text-xs font-medium text-danger hover:opacity-80"
        >
          Удалить
        </button>
      )}
    </div>
  );
}

function SeriesRow({
  examId,
  row,
  onDelete,
}: {
  examId: string;
  row: Extract<Row, { kind: 'series' }>;
  onDelete?: (ids: string[], label: string) => void;
}) {
  const modality = row.files[0].modality || 'DICOM';
  const bytes = row.files.reduce((s, f) => s + (f.originalSize || 0), 0);
  const href = `/viewer/series/${examId}/${encodeURIComponent(row.seriesUid)}`;
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <a href={href} className="shrink-0">
        <FileThumb label="СЕРИЯ" />
      </a>
      <div className="min-w-0 flex-1">
        <a href={href} className="block truncate font-medium text-ink hover:text-accent">
          {modality} · серия ({row.files.length} срезов)
        </a>
        <span className="text-xs text-muted">DICOM · том · {formatBytes(bytes)}</span>
      </div>
      <a href={href} className="shrink-0 text-xs font-medium text-accent hover:text-accent-600">
        Просмотр
      </a>
      {onDelete && (
        <button
          type="button"
          onClick={() => onDelete(row.files.map((f) => f.id), `${modality} серия`)}
          className="shrink-0 text-xs font-medium text-danger hover:opacity-80"
        >
          Удалить
        </button>
      )}
    </div>
  );
}

/** Комментарии к обследованию (под «Снимки») — личные заметки врача:
 *  сервер отдаёт только комментарии текущего пользователя, другим (включая
 *  администратора) они не видны. */
function CommentsSection({ examinationId }: { examinationId: string }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');

  const comments = useQuery({
    queryKey: ['examination', examinationId, 'comments'],
    queryFn: () => listComments(examinationId),
    enabled: Boolean(examinationId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['examination', examinationId, 'comments'] });

  const add = useMutation({
    mutationFn: () => createComment(examinationId, text.trim()),
    onSuccess: () => {
      setText('');
      invalidate();
    },
  });

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-ink">Комментарии</h2>
        <p className="text-xs text-muted">Личные заметки — видны только вам.</p>
      </div>

      {comments.isLoading && <Spinner />}
      {comments.isError && (
        <ErrorText>{apiErrorMessage(comments.error, 'Не удалось загрузить комментарии')}</ErrorText>
      )}

      {comments.data && comments.data.length > 0 && (
        <Card className="divide-y divide-line overflow-hidden">
          {comments.data.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              examinationId={examinationId}
              onChanged={invalidate}
            />
          ))}
        </Card>
      )}

      <div className="space-y-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Добавить комментарий…"
          rows={2}
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted/70 focus-visible:border-accent"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={!text.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Добавление…' : 'Добавить'}
          </Button>
          {add.isError && (
            <span className="text-sm text-danger">
              {apiErrorMessage(add.error, 'Не удалось добавить комментарий')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  examinationId,
  onChanged,
}: {
  comment: ExaminationComment;
  examinationId: string;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);

  const save = useMutation({
    mutationFn: () => updateComment(examinationId, comment.id, draft.trim()),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteComment(examinationId, comment.id),
    onSuccess: onChanged,
  });

  if (editing) {
    return (
      <div className="space-y-2 px-4 py-3">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          autoFocus
          className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus-visible:border-accent"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" disabled={!draft.trim() || save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
          <button
            type="button"
            onClick={() => {
              setDraft(comment.body);
              setEditing(false);
            }}
            className="text-xs font-medium text-muted hover:text-ink"
          >
            Отмена
          </button>
          {save.isError && (
            <span className="text-sm text-danger">
              {apiErrorMessage(save.error, 'Не удалось сохранить')}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1 px-4 py-3">
      <p className="whitespace-pre-wrap text-sm text-ink">{comment.body}</p>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>{formatDateTime(comment.createdAt)}</span>
        {comment.updatedAt !== comment.createdAt && <span>(изменён)</span>}
        <button type="button" onClick={() => setEditing(true)} className="font-medium text-accent hover:text-accent-600">
          Редактировать
        </button>
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm('Удалить комментарий?')) remove.mutate();
          }}
          className="font-medium text-danger hover:opacity-80 disabled:opacity-50"
        >
          Удалить
        </button>
      </div>
      {remove.isError && (
        <p className="text-xs text-danger">{apiErrorMessage(remove.error, 'Не удалось удалить')}</p>
      )}
    </div>
  );
}
